import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { TelegramMessage } from '../types';

interface EditMessageModalProps {
  open: boolean;
  onClose: () => void;
  message: TelegramMessage | null;
  onEdit: (messageId: number, text: string) => Promise<unknown>;
}

export function EditMessageModal({
  open,
  onClose,
  message,
  onEdit,
}: EditMessageModalProps) {
  const [text, setText] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Sync text with message
  useEffect(() => {
    if (message?.text) {
      setText(message.text);
    } else {
      setText('');
    }
  }, [message]);

  const handleSubmit = useCallback(async () => {
    if (!message || !text.trim()) {return;}

    setIsEditing(true);
    try {
      await onEdit(message.id, text.trim());
      onClose();
    } finally {
      setIsEditing(false);
    }
  }, [message, text, onEdit, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const hasChanges = message?.text !== text.trim();
  const canSubmit = hasChanges && text.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Редактировать сообщение</DialogTitle>
          <DialogDescription>
            Внесите изменения в текст сообщения
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Введите текст сообщения..."
            className="min-h-[120px] resize-none"
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            Нажмите Cmd/Ctrl + Enter для сохранения
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isEditing}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isEditing}>
            {isEditing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Сохранение...
              </>
            ) : (
              'Сохранить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
